// Luật kiểm tra khi lưu cấu hình công lương (biến, hằng số, công thức, mẫu, thỏa thuận, phiếu ứng).
// Hàm thuần, chuyển từ các action lưu tương ứng trong VUONGDATWOOD_V2/src/features/cong-luong/action.ts.
import { buildPayrollSeedRows } from './catalog';
import { canonicalPayrollCode, resolveEffectiveDatedRow } from './domain';
import { evaluatePayrollFormulaSet, extractPayrollDependencies, sortPayrollFormulas } from './formulaEvaluator';
import { compilePayrollTemplate } from './templateCompiler';

const RESERVED_WORDS = new Set([
    'IF', 'IFS', 'SWITCH', 'VLOOKUP', 'LOOKUP', 'ROUND', 'ROUNDUP', 'ROUNDDOWN', 'CEILING', 'FLOOR',
    'SUM', 'MIN', 'MAX', 'AVERAGE', 'AND', 'OR', 'ABS', 'TRUE', 'FALSE',
]);

const HE_THONG_SOURCES = ['NANG_SUAT', 'PHEP_NAM', 'NGAY_LE', 'UNG_LUONG', 'HE_THONG'];

export const VARIABLE_SOURCE_LABELS = {
    NANG_SUAT: 'Năng suất',
    NHAP_KY: 'Nhập theo kỳ',
    THOA_THUAN: 'Thỏa thuận',
    PHEP_NAM: 'Phép năm',
    NGAY_LE: 'Ngày lễ',
    UNG_LUONG: 'Ứng lương',
    BHXH_THUE: 'BHXH/Thuế',
    HE_THONG: 'Hằng số công ty',
    CONG_THUC: 'Công thức',
};

export const DATA_TYPE_LABELS = { SO: 'Số', TIEN: 'Tiền', TY_LE: 'Tỷ lệ (%)', TEXT: 'Chữ' };

export const HE_THONG_SOURCE_OPTIONS = HE_THONG_SOURCES;

/** Mã dùng được trong công thức: chữ in hoa, số, gạch dưới, không trùng tên hàm. */
export function validateCode(code, label = 'Mã') {
    if (!code) return `${label} không được để trống.`;
    if (!/^[A-Z_][A-Z0-9_]*$/.test(code)) return `${label} chỉ gồm chữ in hoa không dấu, số và dấu gạch dưới, không bắt đầu bằng số.`;
    if (RESERVED_WORDS.has(code)) return `${label} ${code} trùng tên hàm công thức.`;
    return null;
}

/** Chuẩn hóa dữ liệu form biến; nguồn suy theo loại biến như V2. Trả về { variable, errors }. */
export function normalizeVariableInput(input, { existingCodes = [], isNew = false } = {}) {
    const code = String(input.MA_BIEN ?? '').trim().toUpperCase();
    const kind = input.LOAI_BIEN;
    const formula = String(input.CONG_THUC ?? '').trim().toUpperCase();
    const name = String(input.TEN_BIEN ?? '').trim().toUpperCase();
    const errors = [];
    const codeError = validateCode(code, 'Mã biến');
    if (codeError) errors.push(codeError);
    if (isNew && existingCodes.map(canonicalPayrollCode).includes(code)) errors.push(`Mã biến ${code} đã tồn tại.`);
    if (!name) errors.push('Tên biến không được để trống.');
    if (!['NHAP_TAY', 'HE_THONG', 'THOA_THUAN', 'CONG_THUC'].includes(kind)) errors.push('Chọn loại biến.');
    if (kind === 'CONG_THUC' && !formula) errors.push('Biến công thức bắt buộc phải có công thức.');
    if (kind === 'HE_THONG' && !HE_THONG_SOURCES.includes(input.NGUON_DU_LIEU)) errors.push('Biến hệ thống phải chọn nguồn dữ liệu.');
    const source = kind === 'CONG_THUC' ? 'CONG_THUC' : kind === 'THOA_THUAN' ? 'THOA_THUAN' : kind === 'NHAP_TAY' ? 'NHAP_KY' : input.NGUON_DU_LIEU;
    return {
        errors,
        variable: {
            MA_BIEN: code,
            TEN_BIEN: name,
            LOAI_BIEN: kind,
            NGUON_DU_LIEU: source,
            LOAI_DU_LIEU: input.LOAI_DU_LIEU || 'TIEN',
            DON_VI: String(input.DON_VI ?? '').trim(),
            CONG_THUC: kind === 'CONG_THUC' ? formula : '',
            NHOM: String(input.NHOM ?? '').trim(),
            THU_TU: Number(input.THU_TU) || 0,
            HOAT_DONG: input.HOAT_DONG ?? true,
            MO_TA: String(input.MO_TA ?? '').trim() || name,
        },
    };
}

/**
 * Kiểm tra toàn bộ công thức của danh mục khi thay công thức một biến: biến không tồn tại, vòng lặp,
 * lỗi cú pháp. Gán 1 cho mọi biến không phải công thức (không phải 0) vì nhiều công thức chia cho biến
 * khác như NCC, GIO_CC; gán 0 thì công thức đúng cũng báo chia cho 0. Trả về thông báo lỗi hoặc null.
 */
export function validateFormulaConfiguration(variables, candidate) {
    const list = variables
        .filter((item) => !candidate || canonicalPayrollCode(item.MA_BIEN) !== canonicalPayrollCode(candidate.code))
        .map((item) => ({ code: canonicalPayrollCode(item.MA_BIEN), kind: item.LOAI_BIEN, formula: item.CONG_THUC }));
    if (candidate) list.push({ code: canonicalPayrollCode(candidate.code), kind: candidate.formula ? 'CONG_THUC' : 'HE_THONG', formula: candidate.formula });
    const formulas = Object.fromEntries(list.filter((item) => item.kind === 'CONG_THUC' && item.formula).map((item) => [item.code, item.formula]));
    const available = list.filter((item) => !formulas[item.code]).map((item) => item.code);
    try {
        sortPayrollFormulas(formulas, available);
        evaluatePayrollFormulaSet(formulas, Object.fromEntries(available.map((code) => [code, 1])));
        return null;
    } catch (error) {
        return error instanceof Error ? error.message : 'Công thức không hợp lệ.';
    }
}

/**
 * Biến đang được dùng ở đâu. Còn được dùng thì không xóa hẳn mà chuyển sang ngừng dùng, để số cũ vẫn
 * đọc được và công thức đang chạy không vỡ.
 */
export function findVariableUsages(code, { variables = [], templateColumns = [], agreements = [] }) {
    const target = canonicalPayrollCode(code);
    const reasons = [];
    const inTemplates = new Set(templateColumns.filter((column) => canonicalPayrollCode(column.BIEN_ID) === target).map((column) => column.MAU_ID));
    if (inTemplates.size) reasons.push(`cột của mẫu ${[...inTemplates].join(', ')}`);
    const byVariables = variables
        .filter((item) => canonicalPayrollCode(item.MA_BIEN) !== target && item.CONG_THUC && extractPayrollDependencies(item.CONG_THUC).includes(target))
        .map((item) => item.MA_BIEN);
    if (byVariables.length) reasons.push(`công thức của biến ${byVariables.join(', ')}`);
    const byTemplateFormulas = new Set(templateColumns
        .filter((column) => column.CONG_THUC && extractPayrollDependencies(column.CONG_THUC).includes(target))
        .map((column) => column.MAU_ID));
    if (byTemplateFormulas.size) reasons.push(`công thức riêng của mẫu ${[...byTemplateFormulas].join(', ')}`);
    const agreementCount = agreements.filter((agreement) => Object.keys(agreement.GIA_TRI ?? {}).map(canonicalPayrollCode).includes(target)).length;
    if (agreementCount) reasons.push(`${agreementCount} thỏa thuận lương`);
    return reasons;
}

export function validateDateRange(from, to, label = 'Hiệu lực') {
    if (!from) return `${label} từ ngày không được để trống.`;
    if (to && to < from) return `${label} đến ngày phải sau hoặc bằng ngày bắt đầu.`;
    return null;
}

/** Đời công thức đang áp dụng hôm nay; công thức hiện hành trên danh mục biến luôn là đời này. */
export function effectiveFormulaToday(formulaRows, today = new Date()) {
    return resolveEffectiveDatedRow(
        formulaRows.filter((row) => row.HIEU_LUC_TU).map((row) => ({ ...row, UPDATED_AT: row.UPDATED_AT ?? new Date(0) })),
        today,
    );
}

/** Biến của danh mục ở dạng compilePayrollTemplate cần. */
export function toCompilerVariables(variables) {
    return variables.map((variable) => ({
        id: variable.MA_BIEN,
        code: variable.MA_BIEN,
        name: variable.TEN_BIEN,
        kind: variable.LOAI_BIEN,
        source: variable.NGUON_DU_LIEU,
        formula: variable.CONG_THUC,
        active: variable.HOAT_DONG,
    }));
}

/** Biên dịch mẫu đang soạn để báo lỗi ngay trên form. columns: [{ BIEN_ID, CONG_THUC, BAT_BUOC, HIEN_THI, CHO_PHEP_NHAP }] theo thứ tự. */
export function compileTemplateDraft(columns, variables) {
    return compilePayrollTemplate({
        templateId: 'draft',
        columns: columns.map((column, index) => ({
            variableId: column.BIEN_ID,
            formula: column.CONG_THUC,
            order: index + 1,
            required: column.BAT_BUOC,
            visible: column.HIEN_THI,
            allowInput: column.CHO_PHEP_NHAP,
        })),
        variables: toCompilerVariables(variables),
    });
}

export function validateTemplateInput(template, columns, { variables, existingCodes = [], isNew = false }) {
    const errors = [];
    const code = String(template.MA_MAU ?? '').trim().toUpperCase();
    const codeError = validateCode(code, 'Mã mẫu');
    if (codeError) errors.push(codeError);
    if (isNew && existingCodes.includes(code)) errors.push(`Mã mẫu ${code} đã tồn tại.`);
    if (!String(template.TEN_MAU ?? '').trim()) errors.push('Tên mẫu không được để trống.');
    if (!columns.length) errors.push('Mẫu phải có ít nhất một cột.');
    const compiled = compileTemplateDraft(columns, variables);
    errors.push(...compiled.errors.map((issue) => issue.message));
    return { code, errors, warnings: compiled.warnings.map((issue) => issue.message), compiled };
}

/** Dòng CT_MAU_CONG_LUONG của mẫu, thứ tự đánh lại liên tục theo thứ tự trên form. */
export function buildTemplateColumnRows(templateCode, columns) {
    return columns.map((column, index) => ({
        ID: `${templateCode}|${column.BIEN_ID}`,
        MAU_ID: templateCode,
        BIEN_ID: column.BIEN_ID,
        CONG_THUC: String(column.CONG_THUC ?? '').trim().toUpperCase(),
        THU_TU: index + 1,
        BAT_BUOC: Boolean(column.BAT_BUOC),
        HIEN_THI: column.HIEN_THI !== false,
        CHO_PHEP_NHAP: Boolean(column.CHO_PHEP_NHAP),
    }));
}

const overlaps = (leftFrom, leftTo, rightFrom, rightTo) =>
    (!leftTo || !rightFrom || rightFrom <= leftTo) && (!rightTo || !leftFrom || leftFrom <= rightTo);

/** Thỏa thuận khác của cùng nhân sự có khoảng hiệu lực chồng lên thỏa thuận đang lưu. */
export function findOverlappingAgreements(agreements, candidate) {
    return agreements.filter((agreement) => agreement.ID !== candidate.ID
        && agreement.NHAN_SU_ID === candidate.NHAN_SU_ID
        && overlaps(agreement.HIEU_LUC_TU, agreement.HIEU_LUC_DEN, candidate.HIEU_LUC_TU, candidate.HIEU_LUC_DEN));
}

/**
 * Thỏa thuận cũ còn mở (chưa có ngày kết thúc) và bắt đầu trước thỏa thuận mới thì đóng nó vào ngày
 * trước ngày bắt đầu mới — trường hợp tăng lương thường gặp. Chồng kiểu khác phải sửa tay.
 */
export function planAgreementSuccession(agreements, candidate) {
    const overlapping = findOverlappingAgreements(agreements, candidate);
    const closable = overlapping.filter((agreement) => !agreement.HIEU_LUC_DEN && agreement.HIEU_LUC_TU < candidate.HIEU_LUC_TU);
    const blocking = overlapping.filter((agreement) => !closable.includes(agreement));
    const closeOn = new Date(candidate.HIEU_LUC_TU.getFullYear(), candidate.HIEU_LUC_TU.getMonth(), candidate.HIEU_LUC_TU.getDate() - 1);
    return { closable: closable.map((agreement) => ({ ...agreement, HIEU_LUC_DEN: closeOn })), blocking };
}

/**
 * Giá trị thỏa thuận: chỉ giữ biến thuộc mẫu, báo thiếu biến bắt buộc và tỷ lệ (%) ngoài 0–100,
 * ví dụ tỷ lệ giữ lại PCTN gõ nhầm 500.
 */
export function normalizeAgreementValues(values, agreementCodes, requiredCodes = [], percentCodes = []) {
    const cleaned = {};
    const missing = [];
    const outOfRange = [];
    for (const code of agreementCodes) {
        const raw = values[code];
        const value = raw === '' || raw === null || raw === undefined ? null : Number(raw);
        if (value === null || Number.isNaN(value)) {
            if (requiredCodes.includes(code)) missing.push(code);
            continue;
        }
        if (percentCodes.includes(code) && (value < 0 || value > 100)) outOfRange.push(code);
        cleaned[code] = value;
    }
    return { values: cleaned, missing, outOfRange };
}

/**
 * Dòng khởi tạo còn thiếu so với danh mục mặc định, để nút "Khởi tạo" chỉ thêm phần chưa có mà không
 * đè lên những gì kế toán đã sửa.
 */
export function findMissingSeedRows({ variables = [], constants = [], formulas = [], templates = [], templateColumns = [] }) {
    const seed = buildPayrollSeedRows();
    const variableCodes = new Set(variables.map((item) => canonicalPayrollCode(item.MA_BIEN)));
    const constantCodes = new Set(constants.map((item) => canonicalPayrollCode(item.MA_BIEN)));
    const formulaCodes = new Set(formulas.map((item) => canonicalPayrollCode(item.MA_BIEN)));
    const templateCodes = new Set(templates.map((item) => item.MA_MAU));
    const columnIds = new Set(templateColumns.map((item) => item.ID));
    const missingTemplates = seed.templates.filter((row) => !templateCodes.has(row['MÃ MẪU']));
    const newTemplateCodes = new Set(missingTemplates.map((row) => row['MÃ MẪU']));
    return {
        variables: seed.variables.filter((row) => !variableCodes.has(row['MÃ BIẾN'])),
        constants: seed.constants.filter((row) => !constantCodes.has(row['MÃ BIẾN'])),
        formulas: seed.formulas.filter((row) => !formulaCodes.has(row['MÃ BIẾN'])),
        templates: missingTemplates,
        // Cột chỉ thêm cho mẫu mới tạo; mẫu đã có thì kế toán tự quyết cột.
        templateColumns: seed.templateColumns.filter((row) => newTemplateCodes.has(row['MÃ MẪU']) && !columnIds.has(row.ID)),
    };
}

export function validateAdvanceInput(advance) {
    const errors = [];
    if (!advance.NHAN_SU_ID) errors.push('Chọn nhân sự.');
    if (!advance.NGAY_UNG) errors.push('Chọn ngày ứng.');
    if (!(Number(advance.SO_TIEN) > 0)) errors.push('Số tiền ứng phải lớn hơn 0.');
    return errors;
}
